<script lang="ts">
  /**
   * Application shell and session bootstrap.
   *
   * Order matters: the invite secret is taken out of the URL fragment (and the
   * fragment erased) synchronously during component initialisation, before any
   * markup renders and before the exchange request is made. Everything the
   * user then sees — including the room itself — runs with only a cookie.
   *
   * With no fragment present the app first asks the server whether this
   * browser already holds a session (`GET /api/session`), so a reload or a
   * revisit re-joins silently instead of demanding the invite link again.
   */
  import { installSyncProbe } from '../player/instrumentation';
  import { RoomState } from '../room/state.svelte';
  import { ApiError, exchangeSecret, fetchSession, logoutSession } from '../session/api';
  import { takeInviteSecret } from '../session/invite';
  import { loadUsername, saveUsername } from '../session/username';
  import type { YouInfo } from '../generated';
  import JoinPanel from './JoinPanel.svelte';
  import RoomView from './RoomView.svelte';

  type Stage =
    | { kind: 'checking' }
    | { kind: 'invite'; message: string | null }
    | { kind: 'username'; you: YouInfo }
    | { kind: 'room'; room: RoomState };

  // Consumed before the first render; never stored anywhere else.
  const inviteSecret = takeInviteSecret();

  let stage = $state<Stage>({ kind: 'checking' });
  let busy = $state(false);

  function enterRoom(username: string): void {
    const room = new RoomState(username);
    room.start();
    installSyncProbe(room);
    stage = { kind: 'room', room };
  }

  function afterSession(you: YouInfo): void {
    const remembered = loadUsername();
    if (remembered === null) {
      stage = { kind: 'username', you };
      return;
    }
    enterRoom(remembered);
  }

  async function bootstrap(): Promise<void> {
    busy = true;
    try {
      const you = inviteSecret === null ? await fetchSession() : await exchangeSecret(inviteSecret);
      afterSession(you);
    } catch (error) {
      // No cookie and no invite is the ordinary first-visit case, not an error
      // worth shouting about; a rejected secret is.
      const silent = inviteSecret === null && error instanceof ApiError && error.isUnauthorized;
      stage = {
        kind: 'invite',
        message: silent
          ? null
          : error instanceof ApiError
            ? error.message
            : 'Could not reach the WatchAlong server.',
      };
    } finally {
      busy = false;
    }
  }

  async function joinWithSecret(secret: string): Promise<void> {
    busy = true;
    try {
      afterSession(await exchangeSecret(secret));
    } catch (error) {
      stage = {
        kind: 'invite',
        message:
          error instanceof ApiError ? error.message : 'Could not reach the WatchAlong server.',
      };
    } finally {
      busy = false;
    }
  }

  function chooseUsername(username: string): void {
    saveUsername(username);
    enterRoom(username);
  }

  async function leave(): Promise<void> {
    if (stage.kind === 'room') stage.room.stop();
    try {
      await logoutSession();
    } catch {
      // The cookie may already be invalid; returning to the invite panel is
      // the right outcome either way.
    }
    stage = { kind: 'invite', message: 'You left the room. Open your invite link to re-join.' };
  }

  void bootstrap();
</script>

{#if stage.kind === 'checking'}
  <main class="checking" aria-busy="true">
    <p>Checking your session…</p>
  </main>
{:else if stage.kind === 'room'}
  <RoomView room={stage.room} onLeave={() => void leave()} />
{:else}
  <JoinPanel
    stage={stage.kind}
    message={stage.kind === 'invite' ? stage.message : null}
    suggestedName={stage.kind === 'username' ? stage.you.username : ''}
    {busy}
    onInvite={(secret) => void joinWithSecret(secret)}
    onUsername={chooseUsername}
  />
{/if}

<style>
  .checking {
    max-width: 34rem;
    margin: 0 auto;
    padding: 4rem 1.25rem;
    color: var(--wa-text-dim);
  }
</style>
